import { getTranslations } from "@/lib/translations";
import { z } from "zod";

import { canUserPublishContent, requireAdmin, requireStaff } from "@/core/auth/admin-guard";
import { createPlaceService } from "@/core/services/factory";
import { CONTENT_STATUSES } from "@/core/services/types/article.types";
import {
  DAYS_OF_WEEK,
  PLACE_SEARCH_MAX_LENGTH,
  PLACE_TYPE_VALUES,
  getPlaceSharePath,
  getPlacePublishFieldErrors,
  getPlaceContentPublishFieldErrors,
  isPlaceEditorialChecklistComplete,
  isPlacePublishStatus,
  type PlaceSelectSearchResult,
  type SavePlaceOpeningHoursResult,
  type SyncPlaceOpeningHoursResult,
  type UpdatePlaceState,
  type UpdatePlaceTaxonomyResult,
} from "@/core/services/types/place.types";
import {
  ARTICLE_COVER_IMAGE_MAX_BYTES,
  isAcceptedCoverImageType,
} from "@/core/services/types/media.types";

function parseFieldErrors(
  issues: z.ZodIssue[],
): Record<string, string> {
  const fieldErrors: Record<string, string> = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === "string" && !fieldErrors[field]) {
      fieldErrors[field] = issue.message;
    }
  }
  return fieldErrors;
}

function normalizeRichText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "" || trimmed === "<p></p>") return null;
  return value;
}

function normalizeOptionalText(value: unknown): string | null {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed === "" ? null : trimmed;
}

function normalizeOptionalPlaceType(value: unknown) {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (trimmed === "") return null;
  return trimmed;
}

function mapCoverImageUploadError(
  error: string,
  t: ReturnType<typeof getTranslations>,
): string {
  const normalized = error.toLowerCase();

  if (normalized.includes("row-level security")) {
    return t("coverImageStorageForbidden");
  }

  if (normalized.includes("bucket not found")) {
    return t("coverImageBucketNotFound");
  }

  return t("coverImageUploadFailed");
}

export async function updatePlaceContent(
  _prevState: UpdatePlaceState,
  formData: FormData,
): Promise<UpdatePlaceState> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));
  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  const places = createPlaceService();
  const existing = await places.getForEdit(idResult.data);

  if (!existing) {
    return {
      success: false,
      error: t("updateFailed"),
    };
  }

  const isPublished = existing.status === "PUBLISHED";

  const schema = z.object({
    id: z.uuid(),
    name: isPublished
      ? z.string().trim().min(1, t("nameRequired"))
      : z.preprocess(
          (value) => (typeof value === "string" ? value : ""),
          z.string(),
        ),
    address: z.preprocess(normalizeOptionalText, z.string().nullable()),
    city: z.preprocess(normalizeOptionalText, z.string().nullable()),
    zip_code: z.preprocess(normalizeOptionalText, z.string().nullable()),
    department_name: z.preprocess(normalizeOptionalText, z.string().nullable()),
    website_url: z.preprocess((value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }, z.url(t("invalidWebsiteUrl")).nullable()),
    ticketing_url: z.preprocess((value) => {
      if (typeof value !== "string") return null;
      const trimmed = value.trim();
      return trimmed === "" ? null : trimmed;
    }, z.url(t("invalidTicketingUrl")).nullable()),
    google_place_id: z.preprocess(
      normalizeOptionalText,
      z.string().max(200, t("googlePlaceIdTooLong")).nullable(),
    ),
    latitude: z.coerce.number().min(-90).max(90),
    longitude: z.coerce.number().min(-180).max(180),
    place_type: z.preprocess(
      normalizeOptionalPlaceType,
      z.enum(PLACE_TYPE_VALUES).nullable(),
    ),
    description_short: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
    description_long: z.preprocess(
      normalizeRichText,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    name: formData.get("name"),
    address: formData.get("address"),
    city: formData.get("city"),
    zip_code: formData.get("zip_code"),
    department_name: formData.get("department_name"),
    website_url: formData.get("website_url"),
    ticketing_url: formData.get("ticketing_url"),
    google_place_id: formData.get("google_place_id"),
    latitude: formData.get("latitude"),
    longitude: formData.get("longitude"),
    place_type: formData.get("place_type"),
    description_short: formData.get("description_short"),
    description_long: formData.get("description_long"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, ...content } = parsed.data;

  if (isPublished) {
    const publishFieldErrors = getPlaceContentPublishFieldErrors(content, {
      nameRequired: t("nameRequired"),
      zipCodeRequired: t("zipCodeRequired"),
      coordinatesRequired: t("coordinatesRequired"),
    });

    if (Object.keys(publishFieldErrors).length > 0) {
      return {
        success: false,
        error: t("publishedContentIncomplete"),
        fieldErrors: publishFieldErrors,
      };
    }
  }

  const result = await places.updatePlaceContent(id, content);

  if (!result.success) {
    if (result.error === "duplicate_google_place_id") {
      return {
        success: false,
        error: t("googlePlaceIdAlreadyUsed"),
        fieldErrors: { google_place_id: t("googlePlaceIdAlreadyUsed") },
      };
    }
    return {
      success: false,
      error: t("updateFailed"),
    };
  }
  return {
    success: true,
    data: result.data,
    syncedLinkedEventsCount: result.syncedLinkedEventsCount,
    googlePlaceIdChanged: result.googlePlaceIdChanged,
  };
}

export async function updatePlaceStatus(
  _prevState: UpdatePlaceState,
  formData: FormData,
): Promise<UpdatePlaceState> {
  const user = await requireStaff();

  const t = getTranslations("places.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    status: z.enum(CONTENT_STATUSES),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    status: formData.get("status"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, status } = parsed.data;

  if (isPlacePublishStatus(status) && !canUserPublishContent(user)) {
    return {
      success: false,
      error: t("publishForbidden"),
    };
  }

  const places = createPlaceService();

  if (isPlacePublishStatus(status)) {
    const place = await places.getForEdit(id);

    if (!place) {
      return {
        success: false,
        error: t("statusUpdateFailed"),
      };
    }

    if (!isPlaceEditorialChecklistComplete(place)) {
      return {
        success: false,
        error: t("publishChecklistIncomplete"),
      };
    }

    const publishFieldErrors = getPlacePublishFieldErrors(place, {
      zipCodeRequired: t("zipCodeRequired"),
      coordinatesRequired: t("coordinatesRequired"),
    });

    if (Object.keys(publishFieldErrors).length > 0) {
      return {
        success: false,
        error: t("publishFieldsIncomplete"),
        fieldErrors: publishFieldErrors,
      };
    }
  }

  const updated = await places.updatePlaceStatus(id, { status });

  if (!updated) {
    return {
      success: false,
      error: t("statusUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updatePlaceInternalNotes(
  _prevState: UpdatePlaceState,
  formData: FormData,
): Promise<UpdatePlaceState> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    internal_notes: z.preprocess(
      normalizeOptionalText,
      z.string().nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    internal_notes: formData.get("internal_notes"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, internal_notes } = parsed.data;
  const places = createPlaceService();
  const updated = await places.updatePlaceInternalNotes(id, {
    internalNotes: internal_notes,
  });

  if (!updated) {
    return {
      success: false,
      error: t("internalNotesUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function updatePlaceCoverImage(
  _prevState: UpdatePlaceState,
  formData: FormData,
): Promise<UpdatePlaceState> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const schema = z.object({
    id: z.uuid(),
    cover_image_url: z.preprocess(
      (value) =>
        typeof value === "string" && value.trim() === "" ? null : value,
      z.url(t("invalidCoverImageUrl")).nullable(),
    ),
  });

  const parsed = schema.safeParse({
    id: formData.get("id"),
    cover_image_url: formData.get("cover_image_url"),
  });

  if (!parsed.success) {
    return {
      success: false,
      error: t("fixFields"),
      fieldErrors: parseFieldErrors(parsed.error.issues),
    };
  }

  const { id, cover_image_url } = parsed.data;
  const places = createPlaceService();
  const updated = await places.updatePlaceCoverImage(id, {
    coverImageUrl: cover_image_url,
  });

  if (!updated) {
    return {
      success: false,
      error: t("coverImageUpdateFailed"),
    };
  }
  return { success: true, data: updated };
}

export async function uploadPlaceCoverImage(
  _prevState: UpdatePlaceState,
  formData: FormData,
): Promise<UpdatePlaceState> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");
  const idResult = z.uuid().safeParse(formData.get("id"));
  const file = formData.get("cover_image");

  if (!idResult.success) {
    return {
      success: false,
      error: t("fixFields"),
    };
  }

  if (!(file instanceof File) || file.size === 0) {
    return {
      success: false,
      error: t("coverImageRequired"),
    };
  }

  if (!isAcceptedCoverImageType(file.type)) {
    return {
      success: false,
      error: t("coverImageInvalidType"),
    };
  }

  if (file.size > ARTICLE_COVER_IMAGE_MAX_BYTES) {
    return {
      success: false,
      error: t("coverImageTooLarge"),
    };
  }

  const places = createPlaceService();
  const result = await places.uploadPlaceCoverImage(idResult.data, file);

  if (!result.success) {
    return {
      success: false,
      error: mapCoverImageUploadError(result.error, t),
    };
  }
  return { success: true, data: result.data };
}

export async function syncPlaceOpeningHours(
  _prevState: SyncPlaceOpeningHoursResult,
  formData: FormData,
): Promise<SyncPlaceOpeningHoursResult> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));

  if (!idResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const places = createPlaceService();
  const result = await places.syncOpeningHoursFromGoogle(idResult.data);

  if (!result.success) {
    if (result.error === "No Google Place ID configured") {
      return { success: false, error: t("openingHoursNoGooglePlaceId") };
    }
    return { success: false, error: t("openingHoursSyncFailed") };
  }
  return { success: true, data: result.data };
}

const openingHourEntrySchema = z.object({
  day: z.enum(["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"]),
  isOpen: z.boolean(),
  open: z.string().regex(/^(\d{2}:\d{2})?$/),
  close: z.string().regex(/^(\d{2}:\d{2})?$/),
});

export async function savePlaceOpeningHoursAction(
  _prevState: SavePlaceOpeningHoursResult,
  formData: FormData,
): Promise<SavePlaceOpeningHoursResult> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));
  if (!idResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const rawHours = formData.get("hours");
  const hoursJson = z.string().safeParse(rawHours);
  if (!hoursJson.success) {
    return { success: false, error: t("fixFields") };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(hoursJson.data);
  } catch {
    return { success: false, error: t("fixFields") };
  }

  const hoursResult = z.array(openingHourEntrySchema).length(DAYS_OF_WEEK.length).safeParse(parsed);
  if (!hoursResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const places = createPlaceService();
  const result = await places.savePlaceOpeningHours(idResult.data, hoursResult.data);

  if (!result.success) {
    return { success: false, error: t("openingHoursSaveFailed") };
  }
  return { success: true, data: result.data };
}

export async function updatePlaceTaxonomy(
  _prevState: UpdatePlaceTaxonomyResult,
  formData: FormData,
): Promise<UpdatePlaceTaxonomyResult> {
  await requireStaff();

  const t = getTranslations("places.edit.validation");

  const idResult = z.uuid().safeParse(formData.get("id"));

  if (!idResult.success) {
    return { success: false, error: t("fixFields") };
  }

  const uuidArray = z.array(z.uuid());

  const artFormIds = uuidArray.safeParse(formData.getAll("art_form_ids"));
  const artisticPeriodIds = uuidArray.safeParse(formData.getAll("artistic_period_ids"));
  const culturalAreaIds = uuidArray.safeParse(formData.getAll("cultural_area_ids"));
  const exhibitionTypeIds = uuidArray.safeParse(formData.getAll("exhibition_type_ids"));

  if (!artFormIds.success || !artisticPeriodIds.success || !culturalAreaIds.success || !exhibitionTypeIds.success) {
    return { success: false, error: t("fixFields") };
  }

  const places = createPlaceService();
  const result = await places.updatePlaceTaxonomy(idResult.data, {
    artFormIds: artFormIds.data,
    artisticPeriodIds: artisticPeriodIds.data,
    culturalAreaIds: culturalAreaIds.data,
    exhibitionTypeIds: exhibitionTypeIds.data,
  });

  if (!result.success) {
    return { success: false, error: t("taxonomyUpdateFailed") };
  }
  return { success: true };
}

const placeSelectSearchQuerySchema = z
  .string()
  .trim()
  .min(2)
  .max(PLACE_SEARCH_MAX_LENGTH);

export async function searchPlacesForSelect(
  query: string,
): Promise<PlaceSelectSearchResult> {
  await requireStaff();

  const t = getTranslations("events.edit");
  const parsedQuery = placeSelectSearchQuerySchema.safeParse(query);

  if (!parsedQuery.success) {
    return { success: false, error: t("placeSearchHint") };
  }

  const places = await createPlaceService().searchForSelect(parsedQuery.data);
  return { success: true, places };
}

export async function deletePlaceSignalAction(id: string): Promise<void> {
  await requireAdmin();

  const t = getTranslations("places.edit.statusActions");
  const parsed = z.uuid().safeParse(id);

  if (!parsed.success) {
    throw new Error(t("deleteFailed"));
  }

  const result = await createPlaceService().deleteSignalContent(parsed.data);

  if (!result.success) {
    throw new Error(
      result.error === "not_deletable" ? t("deleteNotAllowed") : t("deleteFailed"),
    );
  }
}
